import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";

import { UpdateBidResponsesInput } from "lib/generated/app-service-gql/graphql";
import { createUseDebounce } from "../../../../lib/debounce";
import { type BidResponseDto } from "../../../../lib/hooks/api/bid/bid_responses/useBidResponse";
import { useUpdateBidResponses } from "../../../../lib/hooks/api/bid/bid_responses/useUpdateBidResponses";
import { useFormAutoSubmit } from "../../../../lib/hooks/useFormAutoSubmit";
import { Flex, Text } from "../../../../styles/utility-components";
import EditableText from "../../../ui/editable_text/EditableText";
import { CompanyUserCombobox } from "../../../ui/form_components/CompanyUserCombobox";
import { BidResponseStageSelect } from "../../../ui/form_components/StageSelect/BidResponseStageSelect";
import DateInput from "../../../ui/input/DateInput";
import toast from "../../../ui/toast/Toast";
import { toDueDateIso } from "../utils";

const textFieldPadding = "8px 8px 8px 14px";

const useDebounce500 = createUseDebounce(500);

// Determine which field was updated for the toast message
const getMessagePrefix = (updates: UpdateBidResponsesInput) => {
  if (updates.reference) return "Reference";
  if (updates.stageId) return "Status";
  if (updates.weighting) return "Weighting";
  if (updates.formattingRequirements) return "Formatting requirements";
  if ("assigneeId" in updates) return "Assignee";
  if ("reviewerId" in updates) return "Reviewer";
  if ("dueDate" in updates) return "Due date";
  return "Bid response";
};

type DetailsFormProps = {
  bidResponse: BidResponseDto;
  onSourceSelect: (source: BidResponseDto["source"]) => void;
};

type FormValues = {
  stageId: string | undefined;
  assigneeId: string | null | undefined;
  reviewerId: string | null | undefined;
  dueDate: Date | string | null;
};

export default function DetailsForm({ bidResponse, onSourceSelect }: DetailsFormProps) {
  const { control, watch, getValues, trigger, reset } = useForm<FormValues>({
    defaultValues: {
      stageId: bidResponse.stageId,
      assigneeId: bidResponse.assignee?.guid ?? null,
      reviewerId: bidResponse.reviewer?.guid ?? null,
      dueDate: bidResponse.dueDate ?? null,
    },
  });

  useEffect(() => {
    reset({
      stageId: bidResponse.stageId,
      assigneeId: bidResponse.assignee?.guid ?? null,
      reviewerId: bidResponse.reviewer?.guid ?? null,
      dueDate: bidResponse.dueDate ?? null,
    });
  }, [
    bidResponse.stageId,
    bidResponse.assignee?.guid,
    bidResponse.reviewer?.guid,
    bidResponse.dueDate,
    reset,
  ]);

  const { mutate: updateBidResponse } = useUpdateBidResponses({
    onSuccess(_data, variables) {
      toast.success(`${getMessagePrefix(variables.input)} updated successfully`);
    },
    onError(_error, variables) {
      toast.error(`Failed to update ${getMessagePrefix(variables.input)}`);
    },
  });

  // For EditableText fields (not form-managed)
  const handleUpdate = async (updates: Partial<UpdateBidResponsesInput>) => {
    if (!bidResponse.id) return;
    updateBidResponse({ input: { ids: [bidResponse.id], ...updates } });
  };

  // Debounced because DateInput's text field emits a change on every parseable keystroke.
  // The other fields here are selects, so they stay immediate.
  const submitDueDate = useDebounce500((value: Date | string | null) => {
    if (!bidResponse.id) return;
    const dueDate = toDueDateIso(value);
    // Both sides normalised, since the cached value may be a Date or an ISO string.
    if (dueDate === toDueDateIso(bidResponse.dueDate)) return;
    updateBidResponse({ input: { ids: [bidResponse.id], dueDate } });
  });

  const handleAutoSubmit = useCallback(
    (data: FormValues, { name }: { name: string }) => {
      if (!bidResponse.id) return;
      if (name === "stageId") {
        updateBidResponse({ input: { ids: [bidResponse.id], stageId: data.stageId } });
      } else if (name === "assigneeId") {
        updateBidResponse({
          input: { ids: [bidResponse.id], assigneeId: data.assigneeId ?? null },
        });
      } else if (name === "reviewerId") {
        updateBidResponse({
          input: { ids: [bidResponse.id], reviewerId: data.reviewerId ?? null },
        });
      } else if (name === "dueDate") {
        submitDueDate(data.dueDate);
      }
    },
    [bidResponse.id, submitDueDate, updateBidResponse],
  );

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: handleAutoSubmit,
    provideMetadata: true,
  });

  return (
    <Section>
      <SectionHeader>
        <Text h2>Details</Text>
      </SectionHeader>
      <Grid>
        <GridItem>
          <FormItemKey>Status</FormItemKey>
        </GridItem>
        <GridItem>
          <BidResponseStageSelect name="stageId" control={control} />
        </GridItem>
        <GridItem>
          <FormItemKey>Reference</FormItemKey>
        </GridItem>
        <GridItem>
          <EditableText
            textProps={{ children: bidResponse.reference || "Not set" }}
            placeholder="Enter the question number / reference"
            emptyText="Not set"
            onSave={(reference) => handleUpdate({ reference })}
            autoFocus={false}
            padding={textFieldPadding}
          />
        </GridItem>
        <GridItem>
          <FormItemKey>Weighting</FormItemKey>
        </GridItem>
        <GridItem>
          <EditableText
            textProps={{ children: bidResponse.weighting || "Not set" }}
            placeholder="Enter weighting"
            emptyText="Not set"
            onSave={(weighting) => handleUpdate({ weighting })}
            autoFocus={false}
            padding={textFieldPadding}
          />
        </GridItem>
        <GridItem>
          <FormItemKey>Formatting requirements</FormItemKey>
        </GridItem>
        <GridItem>
          <EditableText
            textProps={{ children: bidResponse.formattingRequirements || "Not set" }}
            placeholder="Enter formatting requirements"
            emptyText="Not set"
            onSave={(formattingRequirements) => handleUpdate({ formattingRequirements })}
            autoFocus={false}
            padding={textFieldPadding}
          />
        </GridItem>
        <GridItem>
          <FormItemKey>Assigned to</FormItemKey>
        </GridItem>
        <GridItem>
          <CompanyUserCombobox name="assigneeId" control={control} placeholder="Select assignee" />
        </GridItem>
        <GridItem>
          <FormItemKey>Reviewer</FormItemKey>
        </GridItem>
        <GridItem>
          <CompanyUserCombobox name="reviewerId" control={control} placeholder="Select reviewer" />
        </GridItem>
        <GridItem>
          <FormItemKey>Due date</FormItemKey>
        </GridItem>
        <GridItem>
          <DateInput
            name="dueDate"
            control={control}
            calendarProps={{ mode: "single" }}
            triggerWidth="100%"
          />
        </GridItem>
        <GridItem>
          <FormItemKey>Source</FormItemKey>
        </GridItem>
        <GridItem>
          <AlignedText
            as={bidResponse.source?.title ? "a" : "p"}
            onClick={() => onSourceSelect(bidResponse.source)}
          >
            {bidResponse.source?.title || "-"}
          </AlignedText>
        </GridItem>
      </Grid>
    </Section>
  );
}

const Section = styled(Flex)({
  flexDirection: "column",
  gap: 24,
  width: "100%",
  marginTop: 0,
});

const SectionHeader = styled(Flex)({
  flexDirection: "row",
  gap: 8,
  alignItems: "center",
});

const Grid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr 2fr",
  rowGap: 16,
  columnGap: 16,
  width: "100%",
});

const GridItem = styled.div({
  alignContent: "start",
  minHeight: 32,
});

const FormItemKey = styled(Flex)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
  padding: "4px 0",
  alignItems: "center",
  gap: 4,
}));

const AlignedText = styled(Text)({
  display: "block",
  padding: textFieldPadding,
});
